import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { CandidatoDelDia, SitioDeLaOrden } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { useOpcionesZonas } from '@/shared/datos/useOpciones'

import { hora as horaCorta } from '@/modules/tablero/semana'

import { useOcupados, type Ocupacion } from './useOcupados'
import { Modal } from '@/shared/ui/Modal/Modal'

import { HerramientasDeLaOrden } from './HerramientasDeLaOrden'
import { MapaDelSitio } from './MapaDelSitio'
import { useAjustarZona } from './useAjustarZona'
import {
  useAsignarHerramientas,
  useHerramientasDeLaOrden,
  useHerramientasDisponibles,
  useHerramientasRequeridas,
  type Eleccion,
} from './useHerramientas'
import { useCambiarZona } from './useCambiarZona'
import { useMoverPunto } from './useMoverPunto'
import { HorarioObra } from '@/shared/ui/HorarioObra/HorarioObra'

import { useCandidatos, useProgramar, useUbicarEsta } from './useProgramar'
import estilos from './Programar.module.css'

type Props = {
  abierto: boolean
  ordenTrabajoId: string
  ordenTrabajoNumero: string
  ordenServicioId: string
  /** Lo que la cotización proyectó. Es el punto de partida de las horas. */
  horasProyectadas: number | null
  /**
   * Quién viene ya elegido, y desde dónde.
   *
   * Cuando se llega arrastrando un técnico sobre el punto del mapa, el «quién»
   * ya está decidido con el gesto: el diálogo abre con esa persona marcada y lo
   * que queda por decir es cuándo. Sin esto habría que volver a elegirlo en la
   * lista, que es deshacer el gesto que se acaba de hacer.
   */
  colaboradorInicial?: string | null
  fechaInicial?: string | null
  /**
   * La hora que trae el gesto. Cuando se reparte arrastrando sobre el riel, el
   * sitio donde se suelta YA dice a qué hora: obligar a escogerla otra vez en
   * un desplegable desharía la mitad de lo que se acaba de decir.
   */
  horaInicial?: string | null
  onCerrar: () => void
}

/**
 * Estación 3 · Programar.
 *
 * Tres cosas juntas —quién, cuántas horas y cuándo— porque la base no deja
 * pasar una orden a programada sin las tres, y porque sin ellas no hay contra
 * qué medir nada después.
 *
 * MEGA pidió el mapa aquí: «el proyecto cae en una dirección pero debemos
 * mostrársela en el mapa a la hora de hacer la programación». El mapa no está
 * de adorno: al lado va quién ya va a estar cerca ese día, y al elegir a
 * alguien se dibuja la línea entre su trabajo y este.
 */
export function Programar({
  abierto,
  ordenTrabajoId,
  ordenTrabajoNumero,
  ordenServicioId,
  horasProyectadas,
  colaboradorInicial = null,
  fechaInicial = null,
  horaInicial = null,
  onCerrar,
}: Props) {
  const hoy = new Date()
  const manana = new Date(hoy.getTime() + 24 * 60 * 60 * 1000)

  const [fecha, setFecha] = useState(fechaInicial ?? manana.toISOString().slice(0, 10))
  const [hora, setHora] = useState(horaInicial ?? '07:00')
  const [horas, setHoras] = useState(String(horasProyectadas ?? 8))
  const [elegido, setElegido] = useState<string | null>(colaboradorInicial)
  const [error, setError] = useState<string | null>(null)

  const { sitio, cargando: cargandoSitio } = useSitio(abierto ? ordenServicioId : null)
  const { candidatos, cargando } = useCandidatos(abierto ? ordenServicioId : null, fecha)

  const programar = useProgramar()
  const ubicar = useUbicarEsta()
  const ajustar = useAjustarZona()
  const mover = useMoverPunto()
  const cambiarZona = useCambiarZona()
  const { opciones: zonas } = useOpcionesZonas()

  // Arrancan con lo que tiene la zona y se sincronizan cuando llega el sitio.
  const [radioZona, setRadioZona] = useState('')
  const [esRural, setEsRural] = useState(false)

  // ── La herramienta que se lleva ──────────────────────────────────────────
  const { herramientas } = useHerramientasDisponibles()
  const { yaTiene } = useHerramientasDeLaOrden(abierto ? ordenTrabajoId : null)
  const { requeridas: sugeridasPorTecnico } = useHerramientasRequeridas(
    abierto ? ordenTrabajoId : null
  )
  const asignarHerramientas = useAsignarHerramientas()
  const [eleccion, setEleccion] = useState<Eleccion>({})

  // Al abrir se marcan las que la orden ya tenía apartadas.
  // 0318: Si no tiene ninguna todavía, se precargan las que el técnico sugirió al cotizar.
  useEffect(() => {
    if (!abierto) return
    if (Object.keys(yaTiene).length > 0) {
      setEleccion(yaTiene)
    } else if (Object.keys(sugeridasPorTecnico).length > 0) {
      setEleccion(sugeridasPorTecnico)
    } else {
      setEleccion({})
    }
  }, [abierto, yaTiene, sugeridasPorTecnico])

  useEffect(() => {
    if (!sitio) return
    setRadioZona(String(sitio.zona_radio_m ?? sitio.radio_m))
    setEsRural(sitio.zona_rural)
  }, [sitio])

  const candidato = candidatos.find((c) => c.colaborador_id === elegido) ?? null

  // Que a nadie le falte usuario y que le falte a uno son dos avisos distintos.
  const ninguno = candidatos.length > 0 && candidatos.every((c) => !c.tiene_usuario)

  // El fin lo calcula la BASE sobre el horario operativo: salta el almuerzo,
  // para al cerrar la jornada y sigue al siguiente día hábil. Aquí se sumaban
  // las horas de corrido, así que un trabajo de 5 h que arrancaba a las 11
  // decía "16:00" cuando en la calle termina a las 17:00. La cuenta vive en un
  // solo lado para que la pantalla no pueda contradecir a lo que se guarda.
  const inicioISO = useMemo(() => {
    const numero = Number(horas)
    if (!fecha || !hora || !Number.isFinite(numero) || numero <= 0) return null
    return new Date(`${fecha}T${hora}:00`).toISOString()
  }, [fecha, hora, horas])

  // Quién ya tiene esa franja ocupada: se enseña antes de escoger, no al
  // guardar. Va aquí porque necesita el inicio ya calculado.
  const ocupados = useOcupados(inicioISO, Number(horas) || 0, ordenTrabajoId)

  const { data: fin = null, isFetching: calculandoFin } = useQuery({
    queryKey: ['fin-operativo', inicioISO, horas],
    enabled: Boolean(inicioISO),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Date | null> => {
      const { data, error } = await supabase.rpc('fn_fin_operativo', {
        p_inicio: inicioISO!,
        p_horas: Number(horas),
      })
      if (error || !data) return null
      return new Date(data as string)
    },
  })

  async function confirmar() {
    setError(null)

    if (!elegido) return setError('Falta decir quién lo va a hacer.')
    if (!fin) return setError('Las horas tienen que ser un número mayor que cero.')

    try {
      await programar.mutateAsync({
        ordenTrabajoId,
        colaboradorId: elegido,
        horas: Number(horas),
        inicio: new Date(`${fecha}T${hora}:00`).toISOString(),
        fin: fin.toISOString(),
      })

      // LAS HERRAMIENTAS VAN DESPUÉS Y APARTE, y no es un descuido.
      //
      // Programar es lo que no puede fallar: si la asignación de herramientas
      // se cayera —porque entre que se abrió el modal y se confirmó, otra
      // persona se llevó la última escalera— hacerlo todo en una sola
      // operación dejaría la orden SIN PROGRAMAR por culpa de una escalera.
      // El trabajo queda programado, y lo de la herramienta se avisa.
      try {
        await asignarHerramientas.mutateAsync({ ordenTrabajoId, eleccion })
      } catch (falloHerramienta) {
        setError(
          `La orden quedó programada, pero la herramienta no: ${
            falloHerramienta instanceof Error ? falloHerramienta.message : 'error'
          } Ajústala y vuelve a guardar.`,
        )
        return
      }

      onCerrar()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible programar.')
    }
  }

  return (
    <Modal
      abierto={abierto}
      ancho
      titulo={`Programar ${ordenTrabajoNumero}`}
      subtitulo="Quién, cuántas horas y cuándo. Las tres juntas."
      onCerrar={onCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => void confirmar()}
            trabajando={programar.isPending || asignarHerramientas.isPending}
          >
            Programar
          </Boton>
        </>
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      <div className={estilos.cuando}>
        <label className={estilos.campo}>
          <span>Día</span>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </label>

        <label className={estilos.campo}>
          <span>Hora de inicio</span>
          <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
        </label>

        <label className={estilos.campo}>
          <span>Horas</span>
          <input
            type="number"
            min="0.5"
            step="0.5"
            value={horas}
            onChange={(e) => setHoras(e.target.value)}
          />
        </label>

        <div className={estilos.calculado}>
          <span>Termina</span>
          <b>
            {calculandoFin && !fin
              ? '…'
              : fin
              ? fin.toLocaleString('es-CO', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
          </b>
          {horasProyectadas !== null && Number(horas) !== horasProyectadas && (
            <span className={estilos.ojo}>La cotización proyectó {horasProyectadas} h</span>
          )}
        </div>

        {/* Los bloques del día con el almuerzo a la vista: una obra de 8 h no es
            un bloque corrido —a mediodía se para—, y así se ve separado. */}
        {inicioISO && Number(horas) > 0 && (
          <div className={estilos.horario}>
            <HorarioObra inicio={inicioISO} horas={Number(horas)} />
          </div>
        )}
      </div>

      <div className={estilos.dos}>
        {/* ── Dónde cae el trabajo ────────────────────────────────────── */}
        <section className={estilos.lado}>
          <header className={estilos.tituloLado}>
            <h4>Dónde cae</h4>
            {sitio && <p className={estilos.direccion}>{sitio.direccion}</p>}
          </header>

          {cargandoSitio ? (
            <p className={estilos.esperando}>Buscando la dirección…</p>
          ) : !sitio ? (
            <p className={estilos.esperando}>No se pudo leer el sitio de esta orden.</p>
          ) : (
            <>
              {/* EL MAPA SE MUESTRA SIEMPRE, tenga punto o no. Antes, sin
                  punto solo había un botón que le preguntaba a Google — y
                  Google no conoce «Sendero Campestre casa 9», así que ahí se
                  acababa el camino. Quien programa conoce el sector: que lo
                  ponga él tocando el mapa. */}
              <MapaDelSitio
                sitio={
                  sitio.latitud !== null && sitio.longitud !== null
                    ? { latitud: Number(sitio.latitud), longitud: Number(sitio.longitud) }
                    : null
                }
                // Sin punto propio se abre sobre la zona; y si la zona tampoco
                // tiene, sobre el parque de Cartago.
                centro={{
                  latitud: Number(sitio.zona_latitud ?? 4.7466),
                  longitud: Number(sitio.zona_longitud ?? -75.9116),
                }}
                onMover={(latitud, longitud) =>
                  void mover.mutateAsync({ ordenId: sitio.id, latitud, longitud })
                }
                radioM={sitio.radio_m}
                direccion={sitio.direccion}
                cerca={
                  candidato?.cerca_de_latitud && candidato.cerca_de_longitud
                    ? {
                        latitud: candidato.cerca_de_latitud,
                        longitud: candidato.cerca_de_longitud,
                        etiqueta: `${candidato.colaborador} · ${candidato.cerca_de_numero}`,
                      }
                    : null
                }
              />

              {/* El círculo dibujado ES la geocerca. Si le queda obviamente
                  mal, este es el momento en que el Gerente se da cuenta — y
                  por tanto el único momento en que de verdad lo va a
                  corregir. Mandarlo a una pantalla de configuración a
                  acordarse después es garantizar que no se corrija nunca. */}
              <p className={estilos.pieMapa}>
                {sitio.latitud === null ? (
                  <>Sin ubicar: no hay geocerca, y nadie sabe quién le queda cerca.</>
                ) : (
                  <>
                    El círculo de <b>{sitio.radio_m} m</b> es con lo que se van a contar las horas.{' '}
                    {sitio.por_que}.
                  </>
                )}
              </p>

              <div className={estilos.buscarDireccion}>
                <button
                  type="button"
                  className={estilos.enlaceBuscar}
                  disabled={ubicar.isPending}
                  onClick={() =>
                    void ubicar.mutateAsync({
                      id: sitio.id,
                      direccion: sitio.direccion,
                      ciudad: sitio.ciudad,
                      departamento: sitio.departamento,
                    })
                  }
                >
                  {ubicar.isPending ? 'Buscando…' : 'Buscar la dirección en Google'}
                </button>

                {ubicar.isSuccess && ubicar.data === null && (
                  <span className={estilos.noEncontrada}>
                    Google no encontró «{sitio.direccion}». Póngalo a mano en el mapa.
                  </span>
                )}

                {ubicar.isError && (
                  <span className={estilos.noEncontrada}>{(ubicar.error as Error).message}</span>
                )}
              </div>

              {sitio.manda_la_zona && sitio.zona_id && (
                <div className={estilos.ajusteZona}>
                  <span className={estilos.ajusteTexto}>
                    Aquí el radio lo pone la zona, no la dirección.
                  </span>

                  {/* La zona va PRIMERO: con la zona equivocada, tocar el
                      radio de abajo le cambiaría los metros a un barrio que no
                      tiene nada que ver con esta orden. */}
                  <label className={estilos.ajusteCampo}>
                    Zona
                    <select
                      className={estilos.selectorZona}
                      value={sitio.zona_id}
                      disabled={cambiarZona.isPending}
                      onChange={(e) =>
                        void cambiarZona.mutateAsync({
                          ordenServicioId: sitio.id,
                          zonaId: e.target.value,
                        })
                      }
                    >
                      {zonas.map((zona) => (
                        <option key={zona.valor} value={zona.valor}>
                          {zona.etiqueta}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className={estilos.ajusteMandos}>
                    <label className={estilos.ajusteCampo}>
                      Radio
                      <input
                        type="number"
                        min="50"
                        max="2000"
                        step="10"
                        value={radioZona}
                        onChange={(e) => setRadioZona(e.target.value)}
                      />
                      m
                    </label>

                    <label className={estilos.ajusteCasilla}>
                      <input
                        type="checkbox"
                        checked={esRural}
                        onChange={(e) => setEsRural(e.target.checked)}
                      />
                      Es vereda
                    </label>

                    <button
                      type="button"
                      className={estilos.ajusteGuardar}
                      disabled={ajustar.isPending}
                      onClick={() =>
                        void ajustar.mutateAsync({
                          zonaId: sitio.zona_id!,
                          radioM: Number(radioZona) || null,
                          rural: esRural,
                        })
                      }
                    >
                      {ajustar.isPending ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* ── Quién puede ─────────────────────────────────────────────── */}
        <section className={estilos.lado}>
          <header className={estilos.tituloLado}>
            <h4>Quién</h4>
            <p className={estilos.direccion}>
              Ordenados por lo cerca que van a estar ese día
            </p>
          </header>

          {cargando ? (
            <p className={estilos.esperando}>Mirando la agenda del día…</p>
          ) : (
            <>
              {/* Hoy ningún colaborador tiene usuario. Repetirlo en las siete
                  tarjetas no informa: es una sola cosa que falta, y se dice
                  una vez. Cuando sea la excepción, la marca la tarjeta. */}
              {ninguno && (
                <p className={estilos.faltaUsuario}>
                  Ninguno tiene usuario todavía, así que nadie va a poder marcar llegada ni
                  reportar ubicación. Se programa igual; los usuarios se vinculan en
                  Configuración · Colaboradores.
                </p>
              )}

              <ul className={estilos.candidatos}>
                {candidatos.map((quien) => (
                  <Candidato
                    key={quien.colaborador_id}
                    quien={quien}
                    elegido={elegido === quien.colaborador_id}
                    horasNuevas={Number(horas) || 0}
                    marcarSinUsuario={!ninguno}
                    choque={ocupados[quien.colaborador_id] ?? null}
                    onElegir={() => setElegido(quien.colaborador_id)}
                  />
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {/* Al final del modal a propósito: primero se decide QUÉ trabajo, QUIÉN
          y CUÁNDO —que es lo que la base exige para programar— y solo después
          qué se lleva. Puesto arriba interrumpiría la decisión principal con
          una lista de catorce casillas. */}
      <HerramientasDeLaOrden
        herramientas={herramientas}
        yaTiene={yaTiene}
        sugeridas={sugeridasPorTecnico}
        eleccion={eleccion}
        onCambiar={setEleccion}
      />
    </Modal>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/** Dónde cae el trabajo. Se pide aparte porque también sirve sin candidatos. */
function useSitio(ordenServicioId: string | null) {
  const consulta = useQuery({
    queryKey: ['sitio-de-la-orden', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<SitioDeLaOrden | null> => {
      const { data, error } = await supabase
        .from('vista_sitio_de_la_orden')
        .select('*')
        .eq('id', ordenServicioId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return data as unknown as SitioDeLaOrden | null
    },
  })

  return { sitio: consulta.data ?? null, cargando: consulta.isLoading }
}

type PropsCandidato = {
  quien: CandidatoDelDia
  elegido: boolean
  /** Las horas que se le van a sumar. Ocho más ocho no caben en un día. */
  horasNuevas: number
  /** Falso cuando a nadie le falta: entonces el aviso va una sola vez arriba. */
  marcarSinUsuario: boolean
  /** La orden que ya tiene a esa hora. Null = está libre. */
  choque: Ocupacion | null
  onElegir: () => void
}

function Candidato({
  quien,
  elegido,
  horasNuevas,
  marcarSinUsuario,
  choque,
  onElegir,
}: PropsCandidato) {
  const total = quien.horas_ese_dia + horasNuevas
  // Ocho horas es la jornada. Pasarse no está prohibido —hay días de doce— pero
  // tiene que verse antes de asignar, no al final del mes en el cronograma.
  const seLePasa = total > 8

  return (
    <li>
      {/* OCUPADO NO SE PUEDE ESCOGER. No se esconde: saber que está y por
          qué no se puede es más útil que una lista más corta y misteriosa. */}
      <button
        type="button"
        className={`${estilos.candidato} ${elegido ? estilos.elegido : ''} ${
          choque ? estilos.ocupado : ''
        }`}
        onClick={onElegir}
        disabled={Boolean(choque)}
        title={choque ? `Ya tiene la ${choque.numero} a esa hora.` : undefined}
      >
        <div className={estilos.quien}>
          <p className={estilos.nombre}>{quien.colaborador}</p>
          <p className={estilos.cargo}>{quien.cargo ?? 'Sin cargo'}</p>
        </div>

        <div className={estilos.senales}>
          {choque && (
            <span className={estilos.pastillaOcupado}>
              ocupado · {choque.numero}
              {choque.desde && ` ${horaCorta(choque.desde)}–${horaCorta(choque.hasta)}`}
            </span>
          )}
          {quien.distancia_km !== null ? (
            <span className={`${estilos.pastilla} ${quien.distancia_km <= 2 ? estilos.cerca : ''}`}>
              {quien.distancia_km < 1
                ? `a ${Math.round(quien.distancia_km * 1000)} m`
                : `a ${quien.distancia_km.toLocaleString('es-CO', { maximumFractionDigits: 1 })} km`}
            </span>
          ) : quien.ordenes_ese_dia > 0 ? (
            <span className={estilos.pastilla}>sin ubicar</span>
          ) : (
            <span className={`${estilos.pastilla} ${estilos.libre}`}>libre</span>
          )}

          <span className={`${estilos.carga} ${seLePasa ? estilos.pasado : ''}`}>
            {quien.horas_ese_dia} h
            {horasNuevas > 0 && <> → {total} h</>}
          </span>
        </div>

        <p className={estilos.contexto}>
          {quien.ordenes_ese_dia === 0 ? (
            'Sin nada ese día'
          ) : (
            <>
              {quien.ordenes_ese_dia}{' '}
              {quien.ordenes_ese_dia === 1 ? 'orden ese día' : 'órdenes ese día'}
              {quien.cerca_de_direccion && <> · lo más cerca, {quien.cerca_de_direccion}</>}
            </>
          )}
        </p>

        {/* No bloquea la asignación: la bloquearía cuando salga a campo, que es
            peor. Se dice ahora para que se pueda resolver antes. */}
        {marcarSinUsuario && !quien.tiene_usuario && (
          <p className={estilos.aviso}>
            Sin usuario: no va a poder marcar llegada ni reportar ubicación.
          </p>
        )}
      </button>
    </li>
  )
}
